import type { CSSProperties, ReactNode } from 'react'
import { useId } from 'react'
import { cn } from '@/shared/lib/cn'
import s from './SectionCard.module.css'

type SectionCardProps = {
  /**
   * Подпись-eyebrow в шапке секции. **Необязательна:** у первой секции карточки брони
   * («Шапка брони», prototype/Prototype.dc.html:1491) шапки нет вовсе — имя гостя стоит
   * прямо в теле как `<h1>`. Без eyebrow секция рисуется одной рамкой, без `<header>`.
   */
  eyebrow?: ReactNode
  aside?: ReactNode
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

/**
 * Каркас секции — рамка, радиус, тень и появление `wdg-in`. Снят с секций левой колонки
 * карточки брони, где их шесть и все шесть одинаковы (prototype/Prototype.dc.html:1491).
 *
 * Ступенчатая задержка появления задаётся кастомным свойством `--wdg-delay` — см. CSS,
 * там же объяснено, почему именно свойством, а не классом с `animation-delay`.
 */
export function SectionCard({ eyebrow, aside, className, style, children }: SectionCardProps) {
  const eyebrowId = useId()

  return (
    <section
      className={cn(s.root, className)}
      style={style}
      /*
       * Имя секции — её eyebrow. Когда eyebrow нет, ссылаться не на что: `aria-labelledby`,
       * указывающий в несуществующий id, оставил бы секцию БЕЗ имени и вдобавок соврал бы
       * дереву доступности. Безымянная `<section>` — это просто контейнер, и это честно.
       */
      aria-labelledby={eyebrow === undefined ? undefined : eyebrowId}
    >
      {eyebrow !== undefined && (
        <header className={s.header}>
          <span id={eyebrowId} className={s.eyebrow}>
            {eyebrow}
          </span>
          {aside !== undefined && <span className={s.aside}>{aside}</span>}
        </header>
      )}
      {children}
    </section>
  )
}
